import { C_Correspondence_Language } from '../../../../../../../graphql/generatedModel';
import { enumsDe, enumsEn, enumsFr, enumsIt } from '../../../../../../../shared/i18n/enums.i18n';

/**
 * Turns an enum label written in an imported file back into its enum member.
 *
 * The import tab used to do this with fourteen near-identical helpers, each
 * comparing the cell against `t('enums.<member>')` — the label of the *active*
 * UI language only. A file prepared in one language therefore lost every enum
 * value when the operator's UI was in another, and in production the language
 * is not even chosen by the operator: it comes from their `CultureName` and
 * falls back to German. Worse, the tab reads its data in a `useEffect([])`
 * closure, so the language that mattered was the one active when the tab
 * mounted, not the one the operator was looking at (WA-3846).
 *
 * Matching against all four label maps directly removes both problems: the
 * result no longer depends on the active language, nor on i18n being
 * initialised at all.
 */

const LABEL_MAPS = [enumsDe.enums, enumsEn.enums, enumsFr.enums, enumsIt.enums] as Array<
  Record<string, string | object>
>;

/**
 * Labels are compared without diacritics, case or whitespace. Whitespace goes
 * entirely rather than being collapsed because the maps themselves disagree
 * about it — the English label of DNP2 reads "price WaWiAssist(Standard)"
 * while every other language spaces the bracket. Two distinct labels never
 * differ by whitespace alone, so nothing becomes ambiguous.
 */
const fold = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '')
    .toUpperCase();

/**
 * Some labels name the same thing twice, one of them parenthesised:
 * `C_Correspondence_Language` reads "French (Français)" and is spelled that way
 * in all four maps, because the language names are not themselves translated.
 * A customer filling that column writes "Français", or "French" — never the
 * compound — so both halves count as the label. Only members of the enum being
 * decoded are ever compared, so a half can at worst be ambiguous with a
 * sibling, and none of the current enums have overlapping halves.
 */
const labelVariants = (label: string): string[] => {
  const parenthesised = label.match(/^(.*?)\s*\(([^()]*)\)\s*$/);
  if (!parenthesised) return [label];

  const [, outside, inside] = parenthesised;
  return [label, outside, inside].filter((variant) => variant.trim() !== '');
};

/**
 * Labels the four bundles do not actually translate.
 *
 * `C_Correspondence_Language` is spelled "German (Deutsch)" / "French
 * (Français)" in de, en, fr and it alike — the language names were never
 * localised. Matching the bundles therefore accepts a German file that says
 * "Deutsch" and a French one that says "Français", but not the far more likely
 * cross-language cell: a French file naming the German language writes
 * "Allemand", a German one naming French writes "Französisch". Neither is a
 * label of anything, so the value was dropped and the column imported empty —
 * which is what Zenhäusern Frères saw for the very field this ticket adds.
 *
 * The endonyms and the two-letter ISO codes below are what actually appears in
 * customer files; the bare letters are the Swiss convention (D / F / I / E) and
 * are unambiguous inside this one enum.
 */
const EXTRA_LABELS: Record<string, readonly string[]> = {
  [C_Correspondence_Language.CL1_ENGLISH]: ['Englisch', 'Anglais', 'Inglese', 'EN', 'E'],
  [C_Correspondence_Language.CL2_GERMAN]: ['Allemand', 'Tedesco', 'DE', 'D'],
  [C_Correspondence_Language.CL3_FRENCH]: ['Französisch', 'Francese', 'FR', 'F'],
  [C_Correspondence_Language.CL4_ITALIAN]: ['Italienisch', 'Italien', 'IT', 'I'],
};

export const decodeEnumLabel = <T extends string>(
  value: string | null | undefined,
  members: Record<string, T>,
): T | undefined => {
  if (!value) return undefined;

  const target = fold(value);
  if (!target) return undefined;

  for (const member of Object.values(members)) {
    // A file may also carry the member itself, which is what our own exports
    // write into the "value" column.
    if (fold(member) === target) return member;

    if (EXTRA_LABELS[member]?.some((label) => fold(label) === target)) return member;

    for (const labels of LABEL_MAPS) {
      const label = labels[member];
      if (typeof label !== 'string') continue;
      if (labelVariants(label).some((variant) => fold(variant) === target)) return member;
    }
  }

  return undefined;
};
